import React, { Component } from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function EngineBayDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Engine Bay Detailing',
    serviceType: 'Engine Bay Cleaning & Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks', 'Ventura County'],
    description: 'Professional mobile engine bay detailing in Ventura County. Safe degreasing, cleaning, and dressing of your engine compartment.'
  };
  const faqs = [{
    question: 'Is engine bay detailing safe for my car?',
    answer: 'Yes. We carefully protect sensitive electrical components and use low-pressure, controlled cleaning with appropriate degreasers. Our experienced technicians know exactly which areas to avoid, keeping your engine safe throughout the process.'
  }, {
    question: 'Why should I get my engine bay detailed?',
    answer: 'A clean engine bay makes it far easier to spot leaks and worn components, helps prevent grime buildup that traps heat, and dramatically improves resale value and presentation when selling your vehicle.'
  }, {
    question: 'How much does engine bay detailing cost in Ventura County?',
    answer: 'Engine bay detailing is often added to a full detail or booked on its own. Pricing depends on the size and condition of the engine bay. Call (818) 381-9905 for a quick quote.'
  }, {
    question: 'Do you come to me for engine bay detailing?',
    answer: 'Yes. Like all our services, engine bay detailing is fully mobile. We bring our own water and detailing equipment to your home or office anywhere in Ventura County. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Engine Bay Detailing',
    item: 'https://www.fulldetailpro.com/services/engine-bay-detailing'
  }];
  return <ServicePageLayout title="Engine Bay Detailing Ventura County" subtitle="Safe, professional engine bay cleaning that comes to you. Restore a clean, presentable engine compartment without risking sensitive components." metaDescription="Engine bay detailing in Ventura County. Safe mobile degreasing & cleaning of your engine compartment at your home or office. Call Mike's Pro Detail (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-758" data-mp="1et68dzfdsbv2">
      <p data-id="element-759" data-mp="1ssxk1tifdg5w">
        A clean engine bay is the finishing touch on a truly detailed vehicle.
        Our <strong data-id="element-760" data-mp="2q8be7cat6qiw">engine bay detailing in Ventura County</strong> safely
        removes years of grease, dust, and grime from your engine compartment,
        leaving it clean, presentable, and easier to maintain. We perform this
        service on-site as part of our mobile operation, serving Camarillo,
        Oxnard, Ventura, Thousand Oaks, and the surrounding communities.
      </p>

      <h2 data-id="element-761" data-mp="32qf8cd0uenup">Our Engine Bay Detailing Process</h2>
      <p data-id="element-762" data-mp="32pm8lw0awym2">
        Engine bay cleaning requires care and experience. We protect sensitive
        electrical components, use controlled low-pressure rinsing, and apply
        the right products for each surface:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-763" data-mp="3bozi2oofdn6s">
        <li className="flex items-start" data-id="element-764" data-mp="3gpesb7deuekj">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-765" data-mp="3goc10yc6ea43" />
          <span data-id="element-766" data-mp="3li7xtxgpdqxo">
            <strong data-id="element-767" data-mp="3lhorle0f0qzh">Component Protection:</strong> We cover and protect exposed
            electrical connections and sensitive parts before any cleaning
            begins.
          </span>
        </li>
        <li className="flex items-start" data-id="element-768" data-mp="3ujeimiqdxnwq">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-769" data-mp="3b0lplxjjzsr" />
          <span data-id="element-770" data-mp="397bjwnpyxg0">
            <strong data-id="element-771" data-mp="3ltxp6sczxc5b">Degreasing:</strong> Safe, effective degreasers break down
            built-up oil, grease, and road grime throughout the bay.
          </span>
        </li>
        <li className="flex items-start" data-id="element-772" data-mp="3hq9iebw6ukl4">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-773" data-mp="3vkad8uk0rng7" />
          <span data-id="element-774" data-mp="3qknwwc8w9r36">
            <strong data-id="element-775" data-mp="3qkxkxuadpo68">Detail Brushing & Rinsing:</strong> Hand brushing of tight
            areas followed by a controlled, low-pressure rinse.
          </span>
        </li>
        <li className="flex items-start" data-id="element-776" data-mp="32wwldav6hi2y">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-777" data-mp="2yu9qf137nh1j" />
          <span data-id="element-778" data-mp="3ct9ea6iwfrwn">
            <strong data-id="element-779" data-mp="3ctslas70gvvw">Dressing & Protection:</strong> A non-greasy dressing
            restores plastics and hoses to a clean, factory-fresh finish.
          </span>
        </li>
      </ul>

      <h2 data-id="element-780" data-mp="s2s2ae8ya0bk">Benefits of a Clean Engine Bay</h2>
      <p data-id="element-781" data-mp="s1qucin8dty2">
        Beyond looking great, a detailed engine bay makes it easier to identify
        leaks and worn components, helps prevent grime from trapping heat, and
        significantly boosts your vehicle's resale presentation. It's a smart
        addition to any{' '}
        <Link to="/services/full-car-detail" data-id="element-782" data-mp="2a0x2ianocmx4">full car detail</Link>.
      </p>

      <h2 data-id="element-783" data-mp="1w15klyu1emdu">Related Mobile Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-784" data-mp="1w2godynhfu4y">
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-785" data-mp="2110aq05zn7xu">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-786" data-mp="2sszcj35t94ye">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-787" data-mp="37nnggcvkbvbw">
          Mobile Detailing &rarr;
        </Link>
        <Link to="/services/headlight-restoration" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-788" data-mp="3hcaujn4x1x3n">
          Headlight Restoration &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}